import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save, Image as ImageIcon
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { ImageUpload } from '../ImageUpload';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

const GALLERY_CATEGORIES = [
  { value: '', label: 'Tutte' },
  { value: 'crags', label: 'Falesie' },
  { value: 'events', label: 'Eventi' },
  { value: 'team', label: 'Team' },
  { value: 'nature', label: 'Natura' },
  { value: 'climbing', label: 'Arrampicata' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}><X className="w-6 h-6" /></button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder }) {
  const [activeLang, setActiveLang] = useState('it');
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (<TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.flag}</TabsTrigger>))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Input value={value?.[lang.code] || ''} onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })} placeholder={`${placeholder} (${lang.name})`} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export function AdminGallery() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchImages();
  }, [isAuthenticated, authLoading, navigate, filterCategory]);

  const fetchImages = async () => {
    try { 
      const url = filterCategory 
        ? `${API}/gallery?category=${filterCategory}&active_only=false` 
        : `${API}/gallery?active_only=false`;
      const res = await axios.get(url); 
      setImages(res.data); 
    }
    catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing.id && images.find(i => i.id === editing.id)) {
        await axios.put(`${API}/gallery/${editing.id}`, editing);
        toast.success('Immagine aggiornata');
      } else {
        await axios.post(`${API}/gallery`, editing);
        toast.success('Immagine aggiunta');
      }
      setShowDialog(false); 
      fetchImages();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa immagine dalla gallery?')) return;
    try { 
      await axios.delete(`${API}/gallery/${id}`); 
      toast.success('Immagine eliminata'); 
      fetchImages(); 
    }
    catch (error) { toast.error('Errore nell\'eliminazione'); }
  };

  const handleUpload = async (uploaded) => {
    try {
      const uploadedImages = Array.isArray(uploaded) ? uploaded : [uploaded];
      
      for (const img of uploadedImages) {
        const galleryImage = {
          url: img.url,
          caption: { it: '', en: '', fr: '', de: '', es: '' },
          category: uploadCategory,
          is_active: true
        };
        await axios.post(`${API}/gallery`, galleryImage);
      }
      
      toast.success(`${uploadedImages.length} immagini aggiunte alla gallery`);
      setShowUploadDialog(false);
      fetchImages();
    } catch (error) {
      toast.error('Errore nell\'aggiunta alla gallery');
    }
  };

  const toggleActive = async (image) => {
    try {
      await axios.put(`${API}/gallery/${image.id}`, { ...image, is_active: !image.is_active });
      toast.success(image.is_active ? 'Immagine nascosta' : 'Immagine visibile');
      fetchImages();
    } catch (error) { toast.error('Errore nel cambio stato'); }
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-gallery">
      <AdminSidebar currentPath="/admin/gallery" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Gallery</h1>
          <Button onClick={() => setShowUploadDialog(true)} className="btn-primary">
            <Upload className="w-4 h-4 mr-2" />Carica Immagini
          </Button>
        </header>

        <main className="p-6">
          {/* Filters */}
          <div className="bg-white rounded-lg border p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {GALLERY_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setFilterCategory(cat.value)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    filterCategory === cat.value 
                      ? 'bg-[#FF6D00] text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : images.length === 0 ? (
            <div className="text-center py-20">
              <Images className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Nessuna immagine nella gallery</p>
              <Button onClick={() => setShowUploadDialog(true)} className="btn-primary">Carica la prima immagine</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {images.map((image) => (
                <div key={image.id} className="group relative bg-white rounded-lg border overflow-hidden">
                  <div className="aspect-square bg-gray-100">
                    <img 
                      src={image.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` : image.url} 
                      alt={image.caption?.it || ''} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button 
                      onClick={() => { setEditing(image); setShowDialog(true); }}
                      className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100"
                    >
                      <Edit className="w-5 h-5 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleDelete(image.id)}
                      className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100"
                    >
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </button>
                  </div>

                  {/* Status badge */}
                  <div className="absolute top-2 left-2">
                    <button
                      onClick={() => toggleActive(image)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        image.is_active 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-500 text-white'
                      }`}
                    >
                      {image.is_active ? 'Visibile' : 'Nascosta'}
                    </button>
                  </div>

                  {/* Category badge */}
                  {image.category && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 rounded text-xs bg-black/60 text-white">
                        {image.category}
                      </span>
                    </div>
                  )}

                  {/* Caption */}
                  {image.caption?.it && (
                    <div className="p-2 border-t">
                      <p className="text-xs text-gray-600 truncate">{image.caption.it}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Modifica Immagine</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-4">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src={editing.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${editing.url}` : editing.url} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <TranslatedInput 
                label="Didascalia" 
                value={editing.caption} 
                onChange={(v) => setEditing({...editing, caption: v})} 
                placeholder="Descrizione immagine" 
              />
              
              <div>
                <label className="block text-sm font-medium mb-2">Categoria</label>
                <select 
                  className="w-full border rounded-lg px-3 py-2"
                  value={editing.category || ''} 
                  onChange={(e) => setEditing({...editing, category: e.target.value})}
                >
                  {GALLERY_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Ordine</label>
                <Input 
                  type="number" 
                  value={editing.order || 0} 
                  onChange={(e) => setEditing({...editing, order: parseInt(e.target.value) || 0})} 
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="is_active"
                  checked={editing.is_active} 
                  onChange={(e) => setEditing({...editing, is_active: e.target.checked})} 
                />
                <label htmlFor="is_active" className="text-sm">Visibile nella gallery pubblica</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Carica Immagini</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">Categoria</label>
              <select 
                className="w-full border rounded-lg px-3 py-2"
                value={uploadCategory} 
                onChange={(e) => setUploadCategory(e.target.value)}
              >
                {GALLERY_CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label || 'Nessuna categoria'}</option>
                ))}
              </select>
            </div>
            
            <ImageUpload
              onUpload={handleUpload}
              category={uploadCategory}
              multiple={true}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
